<template>
  <div class="vx-grafico" :data-test="`grafico-${tipo}`">
    <apexchart
      v-if="listo"
      :type="apexTipo"
      :height="alto"
      :options="opciones"
      :series="series"
    />
    <div v-else class="vx-grafico-vacio">
      <span class="vx-vacio-texto">Sin datos para graficar.</span>
    </div>
  </div>
</template>

<script setup>
// El único lugar del sistema que sabe de ApexCharts — PR-4.
//
// **No se importa la librería en cada vista** y no es manía de arquitectura:
// son ~140KB (D-R4) y cuatro pantallas. Concentrarlo deja un solo sitio donde
// está la paleta, el comportamiento en claro/oscuro y el formato de moneda — y
// un solo sitio donde cambiar de librería si alguna vez pesa demasiado.
//
// **El gráfico se suma ARRIBA de la tabla, nunca la reemplaza** (RN-89): el CSV
// y la tabla son la salida que usa el contador.

import { computed } from 'vue'

// `<apexchart>` viene registrado GLOBAL desde `main.js`. No se importa acá: el
// default de `vue3-apexcharts` es un plugin con `install`, no un componente,
// y usarlo directo como componente local no es lo que la librería documenta.

/* global defineProps */
const props = defineProps({
  // `barras` horizontal (morosidad), `combo` barras+línea (cobranza),
  // `cascada` (utilidad). Son los tres que pide SPEC B.4.
  tipo: { type: String, required: true },
  series: { type: Array, default: () => [] },
  categorias: { type: Array, default: () => [] },
  alto: { type: Number, default: 280 },
  moneda: { type: String, default: 'PYG' },
  // Para el combo: qué serie va como línea y en qué eje.
  serieLinea: { type: String, default: null }
})

const listo = computed(() =>
  props.series.length > 0 && props.series.some((s) => (s.data || []).length > 0))

const apexTipo = computed(() => {
  if (props.tipo === 'cascada') return 'rangeBar'
  if (props.tipo === 'combo') return 'line'
  return 'bar'
})

// Formato de guaraníes sin decimales. El guaraní no usa centavos
// (REGLAS_DE_CONSTRUCCION 4), así que forzarlos en el eje sólo agrega ruido.
const formatoMonto = (v) => {
  const n = Number(v) || 0
  return new Intl.NumberFormat('es-PY', {
    style: 'currency', currency: props.moneda,
    minimumFractionDigits: props.moneda === 'PYG' ? 0 : 2
  }).format(n)
}

// Paleta tomada de las variables del tema para que claro y oscuro no dependan
// de la paleta por defecto de ApexCharts, que es la del modo claro y queda
// ilegible sobre fondo oscuro.
const PALETA = ['#0d6efd', '#dc3545', '#198754', '#fd7e14', '#6f42c1', '#20c997']

const opciones = computed(() => {
  const base = {
    chart: {
      toolbar: { show: false },
      fontFamily: 'inherit',
      background: 'transparent',
      animations: { enabled: false }
    },
    theme: { mode: 'light' },
    colors: PALETA,
    grid: { borderColor: 'rgba(19, 47, 58, 0.10)' },
    dataLabels: { enabled: false },
    legend: { position: 'top', horizontalAlign: 'left' },
    xaxis: { categories: props.categorias },
    tooltip: { y: { formatter: formatoMonto } },
    noData: { text: 'Sin datos para graficar.' }
  }

  if (props.tipo === 'barras') {
    return {
      ...base,
      chart: { ...base.chart, type: 'bar' },
      plotOptions: { bar: { horizontal: true, borderRadius: 3, distributed: true } },
      legend: { show: false },
      yaxis: { labels: { style: { fontSize: '12px' } } },
      xaxis: { ...base.xaxis, labels: { formatter: formatoMonto } }
    }
  }

  if (props.tipo === 'combo') {
    return {
      ...base,
      chart: { ...base.chart, type: 'line', stacked: false },
      stroke: { width: [0, 0, 3] },
      plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
      yaxis: [
        { seriesName: props.series[0]?.name, labels: { formatter: formatoMonto } },
        { seriesName: props.series[0]?.name, show: false },
        {
          opposite: true,
          min: 0,
          max: 1,
          labels: { formatter: (v) => `${Math.round((Number(v) || 0) * 100)}%` }
        }
      ]
    }
  }

  // Cascada. `rangeBar` la dibuja directo —cada escalón es un rango
  // [desde, hasta]— sin el truco de barras flotantes transparentes.
  return {
    ...base,
    chart: { ...base.chart, type: 'rangeBar' },
    plotOptions: { bar: { horizontal: false, borderRadius: 3 } },
    legend: { show: false },
    yaxis: { labels: { formatter: formatoMonto } },
    tooltip: {
      custom: ({ dataPointIndex, w }) => {
        const p = w.config.series[0].data[dataPointIndex]
        const delta = (p.y?.[1] ?? 0) - (p.y?.[0] ?? 0)
        return `<div class="px-2 py-1"><strong>${p.x}</strong><br>${formatoMonto(delta)}</div>`
      }
    }
  }
})
</script>

<style scoped>
.vx-grafico { min-height: 60px; }
.vx-grafico-vacio {
  display: flex; align-items: center; justify-content: center;
  min-height: 120px; color: var(--vx-texto-3, #6c757d);
}
</style>
